import React, { useState, useEffect, useMemo } from 'react';
import { educationApi } from '../api/endpoints';
import KPICard from '../components/KPICard';
import LoadingWait from '../components/LoadingWait';
import SeminarsPanel from '../components/SeminarsPanel';
import TimeSeriesChart from '../components/charts/TimeSeriesChart';

const HL_ROW_BG = 'rgba(34,211,238,.08)';

const YEAR_TABS = [
  { key: '2025', label: '2025' },
  { key: '2026', label: '2026' },
];

const SECTION_TABS = [
  { key: 'report', label: 'Отчёт' },
  { key: 'getcourse', label: 'Геткурс' },
  { key: 'ads', label: 'Реклама Обучений' },
];

const MONTH_ORDER = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

function cellText(cell) {
  if (cell == null) return '';
  if (typeof cell === 'object' && cell.href) return String(cell.text ?? cell.href ?? '');
  return String(cell);
}

function parseEduMoney(v) {
  if (v == null || v === '' || v === '-' || v === '—') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const raw = typeof v === 'object' && v != null && 'text' in v ? v.text : v;
  let s = String(raw).replace(/\u00a0/g, ' ').trim();
  s = s.replace(/^[рp]\.?\s*/i, '').replace(/\s+/g, '').replace(',', '.');
  s = s.replace(/[^\d.\-]/g, '');
  if (!s || s === '-' || s === '.' || s === '-.') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function monthSheetIndex(title) {
  const key = String(title || '').trim().toLowerCase();
  return MONTH_ORDER.indexOf(key);
}

function extractMonthTotals(tab) {
  const data = tab?.data;
  if (!data?.headers?.length || !data?.rows?.length) return null;
  const headers = data.headers.map((h) => String(h || '').trim().toLowerCase());
  const revIdx = headers.findIndex((h) => h.includes('выруч'));
  const costIdx = headers.findIndex((h) => h.includes('затрат'));
  const profitIdx = headers.findIndex((h) => h.includes('прибыл'));
  if (revIdx < 0 && costIdx < 0 && profitIdx < 0) return null;

  for (const row of data.rows) {
    const first = cellText(row[0]).trim().toLowerCase();
    if (!first.includes('итого')) continue;
    return {
      revenue: revIdx >= 0 ? parseEduMoney(row[revIdx]) : null,
      costs: costIdx >= 0 ? parseEduMoney(row[costIdx]) : null,
      profit: profitIdx >= 0 ? parseEduMoney(row[profitIdx]) : null,
    };
  }
  return null;
}

function CellValue({ cell }) {
  if (cell != null && typeof cell === 'object' && cell.href) {
    return (
      <a
        href={cell.href}
        target="_blank"
        rel="noreferrer"
        style={styles.cellLink}
        title={cell.href}
      >
        {cell.text || cell.href}
      </a>
    );
  }
  const text = cellText(cell);
  return text || '';
}

export default function Education() {
  const [year, setYear] = useState('2026');
  const [section, setSection] = useState('report');
  const [tabs, setTabs] = useState([]);
  const [getcourseTab, setGetcourseTab] = useState(null);
  const [adsTabs, setAdsTabs] = useState([]);
  const [active, setActive] = useState(0);
  const [adsActive, setAdsActive] = useState(0);
  const [error, setError] = useState('');
  const [adsError, setAdsError] = useState('');
  const [sourceInfo, setSourceInfo] = useState('');
  const [adsSourceInfo, setAdsSourceInfo] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsLoaded, setAdsLoaded] = useState(false);

  useEffect(() => {
    if (year !== '2026') return;
    setLoading(true);
    educationApi.tabs()
      .then(r => {
        const monthTabs = (r.data.tabs || []).filter((t) => {
          const key = String(t?.title || '').trim().toLowerCase();
          return key && !key.includes('геткурс') && !key.includes('getcourse');
        });
        setTabs(monthTabs);
        setGetcourseTab(r.data.getcourse || null);
        if (r.data.error) setError(r.data.error);
        else setError('');
        const sf = r.data.source_file;
        const sn = r.data.sheet_names;
        if (sf && sn?.length) {
          setSourceInfo(`${sf} · ${sn.length} вкладок`);
        } else if (sf) {
          setSourceInfo(sf);
        } else {
          setSourceInfo('');
        }
      })
      .catch(() => setError('Ошибка загрузки данных обучения'))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (year !== '2026' || section !== 'ads' || adsLoaded) return;
    setAdsLoading(true);
    educationApi.adsTabs()
      .then((r) => {
        setAdsTabs(r.data.tabs || []);
        if (r.data.error) setAdsError(r.data.error);
        else setAdsError('');
        const sf = r.data.source_file;
        const sn = r.data.sheet_names;
        if (sf && sn?.length) setAdsSourceInfo(`${sf} · ${sn.length} листов`);
        else if (sf) setAdsSourceInfo(sf);
        else setAdsSourceInfo('');
        setAdsLoaded(true);
      })
      .catch(() => setAdsError('Ошибка загрузки отчёта по рекламе'))
      .finally(() => setAdsLoading(false));
  }, [year, section, adsLoaded]);

  useEffect(() => {
    if (!infoOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setInfoOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [infoOpen]);

  const isAds = section === 'ads';
  const isGetcourse = section === 'getcourse';
  const current = isAds
    ? adsTabs[adsActive]
    : isGetcourse
      ? getcourseTab
      : tabs[active];
  const data = current?.data;
  const hlSet = new Set(current?.highlight_rows || []);
  const isGrid =
    current?.format === 'grid' ||
    (data?.headers?.length &&
      data?.rows?.[0]?.length === data.headers.length &&
      !(data.row_headers || []).length);

  const rowBg = (ri) => (hlSet.has(ri) ? HL_ROW_BG : 'var(--bg-card)');

  /** Строка «Итого» в grid: первая ячейка содержит «итого» — показываем остальные колонки как KPI. */
  const totalsKpis = useMemo(() => {
    if (!data?.headers?.length || isAds || isGetcourse) return null;
    const headers = data.headers;

    if (isGrid && data.rows?.length) {
      for (const row of data.rows) {
        const first = cellText(row[0]).trim().toLowerCase();
        if (first && first.includes('итого')) {
          const items = [];
          for (let i = 1; i < headers.length && i < row.length; i++) {
            const v = cellText(row[i]);
            items.push({
              title: String(headers[i] || '').trim() || '—',
              value: v !== undefined && v !== null && String(v).trim() !== '' ? String(v) : '—',
            });
          }
          return items.length ? items : null;
        }
      }
    }

    if (!isGrid && data.row_headers?.length && data.rows?.length) {
      for (let ri = 0; ri < data.row_headers.length; ri++) {
        const rh = String(data.row_headers[ri] || '').trim().toLowerCase();
        if (!rh.includes('итого')) continue;
        const row = data.rows[ri] || [];
        const items = [];
        for (let i = 0; i < headers.length && i < row.length; i++) {
          items.push({
            title: String(headers[i] || '').trim() || '—',
            value: cellText(row[i]) || '—',
          });
        }
        return items.length ? items : null;
      }
    }

    return null;
  }, [data, isGrid, isAds, isGetcourse]);

  const yearChartData = useMemo(() => {
    if (year !== '2026' || isAds || isGetcourse) return [];
    const points = [];
    for (const tab of tabs) {
      const title = String(tab?.title || '').trim();
      const key = title.toLowerCase();
      if (!key || key.includes('геткурс') || key.includes('getcourse')) continue;
      const mi = monthSheetIndex(title);
      if (mi < 0) continue;
      const totals = extractMonthTotals(tab);
      if (!totals) continue;
      points.push({
        sort: mi,
        date: title.charAt(0).toUpperCase() + title.slice(1).toLowerCase(),
        revenue: totals.revenue,
        costs: totals.costs,
        profit: totals.profit,
      });
    }
    points.sort((a, b) => a.sort - b.sort);
    return points.map(({ date, revenue, costs, profit }) => ({ date, revenue, costs, profit }));
  }, [tabs, year, isAds, isGetcourse]);

  const getcourseChartData = useMemo(() => {
    if (!isGetcourse || !getcourseTab?.data?.headers?.length || !getcourseTab?.data?.rows?.length) {
      return [];
    }
    const headers = getcourseTab.data.headers.map((h) => String(h || '').trim().toLowerCase());
    const activeIdx = headers.findIndex((h) => h.includes('активн'));
    const newIdx = headers.findIndex((h) => h.includes('нов'));
    const openedIdx = headers.findIndex((h) => h.includes('открыто') || h.includes('отделом продаж'));
    if (activeIdx < 0 && newIdx < 0 && openedIdx < 0) return [];

    const points = [];
    for (const row of getcourseTab.data.rows) {
      const monthRaw = cellText(row[0]).trim();
      const mi = monthSheetIndex(monthRaw);
      if (mi < 0) continue;
      const active = activeIdx >= 0 ? parseEduMoney(row[activeIdx]) : null;
      const neu = newIdx >= 0 ? parseEduMoney(row[newIdx]) : null;
      const opened = openedIdx >= 0 ? parseEduMoney(row[openedIdx]) : null;
      if (active == null && neu == null && opened == null) continue;
      points.push({
        sort: mi,
        date: monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1).toLowerCase(),
        active,
        newUsers: neu,
        opened,
      });
    }
    points.sort((a, b) => a.sort - b.sort);
    return points.map(({ date, active, newUsers, opened }) => ({
      date,
      active,
      newUsers,
      opened,
    }));
  }, [isGetcourse, getcourseTab]);

  const sectionLoading = isAds ? adsLoading : loading;
  const sectionError = isAds
    ? adsError
    : (isGetcourse && !getcourseTab && !loading
      ? (error || 'Лист «Геткурс» не найден в файле обучения')
      : error);
  const sectionSource = isAds ? adsSourceInfo : sourceInfo;
  const sheetTabs = isAds ? adsTabs : (isGetcourse ? [] : tabs);
  const sheetActive = isAds ? adsActive : active;
  const setSheetActive = isAds ? setAdsActive : setActive;

  return (
    <div style={styles.page} className="dashboard-panel-enter">
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.h1}>Обучение</h1>
        </div>
        {year === '2026' && section === 'report' && (
          <button
            type="button"
            className="dash-toolbar-btn"
            style={styles.infoBtn}
            onClick={() => setInfoOpen(true)}
          >
            Подробнее
          </button>
        )}
      </div>
      {infoOpen && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setInfoOpen(false)}
          role="presentation"
        >
          <div
            style={styles.modalPanel}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="education-links-title"
          >
            <div style={styles.modalHeader}>
              <h2 id="education-links-title" style={styles.modalTitle}>Подробнее</h2>
              <button type="button" style={styles.modalClose} onClick={() => setInfoOpen(false)} aria-label="Закрыть">
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <a
                href="https://docs.google.com/spreadsheets/d/1Iz5cp9LVy5KXqKKIEaLFYQSPBxDY8k39mMiaTzAa2Zw/edit?gid=0#gid=0"
                target="_blank"
                rel="noreferrer"
                className="education-link"
                style={styles.linkBtn}
              >
                Подробная таблица
              </a>
              <a
                href="https://docs.google.com/spreadsheets/d/14IvpLwe-81jhO3C5Eq9vY-BC06sLmX-b9g4kY-5DIzU/edit?gid=1132703672#gid=1132703672"
                target="_blank"
                rel="noreferrer"
                className="education-link"
                style={styles.linkBtn}
              >
                Оплаты
              </a>
            </div>
          </div>
        </div>
      )}
      <div style={styles.yearTabsBar} role="tablist" aria-label="Год">
        {YEAR_TABS.map((y) => (
          <button
            key={y.key}
            type="button"
            role="tab"
            aria-selected={year === y.key}
            onClick={() => setYear(y.key)}
            style={{ ...styles.yearTabBtn, ...(year === y.key ? styles.yearTabActive : {}) }}
          >
            {y.label}
          </button>
        ))}
      </div>

      {year === '2025' ? (
        <SeminarsPanel />
      ) : (
        <>
          <div style={styles.sectionTabsBar} role="tablist" aria-label="Разделы обучения">
            {SECTION_TABS.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={section === s.key}
                onClick={() => {
                  setSection(s.key);
                  if (s.key === 'report') setActive(0);
                  if (s.key === 'ads') setAdsActive(0);
                }}
                style={{ ...styles.sectionTabBtn, ...(section === s.key ? styles.sectionTabActive : {}) }}
              >
                {s.label}
              </button>
            ))}
          </div>

          {sectionLoading ? (
            <div style={styles.section} className="dash-section">
              <LoadingWait />
            </div>
          ) : (
            <>
              {sectionSource ? (
                <div style={styles.metaBar}>
                  <span style={styles.metaLabel}>Источник</span>
                  <span style={styles.metaValue}>{sectionSource}</span>
                </div>
              ) : null}
              {sectionError && <div style={styles.error}>{sectionError}</div>}

              {!isAds && !isGetcourse && year === '2026' && yearChartData.length > 0 && (
                <div style={styles.section} className="dash-section">
                  <h2 style={styles.totalsHeading}>2026: выручка, затраты и прибыль по месяцам</h2>
                  <TimeSeriesChart
                    idPrefix="edu-year-2026"
                    data={yearChartData}
                    height={340}
                    lines={[
                      { key: 'revenue', name: 'Выручка', color: 'var(--accent)' },
                      { key: 'costs', name: 'Затраты', color: 'var(--warning)' },
                      { key: 'profit', name: 'Прибыль', color: 'var(--success)' },
                    ]}
                  />
                </div>
              )}

              {isGetcourse && (
                <div style={styles.totalsBlock}>
                  <h2 style={styles.totalsHeading}>Геткурс · 2026</h2>
                  <p style={styles.getcourseHint}>
                    Помесячно: активные, новые, открыто отделом продаж. База и покупки за деньги — отдельно ниже.
                  </p>
                  {getcourseChartData.length > 0 && (
                    <>
                      <div style={{ ...styles.section, marginBottom: 16 }}>
                        <h2 style={styles.totalsHeading}>Динамика: активные и новые пользователи</h2>
                        <TimeSeriesChart
                          idPrefix="edu-getcourse-users"
                          data={getcourseChartData}
                          height={320}
                          lines={[
                            { key: 'active', name: 'Активные', color: 'var(--accent)' },
                            { key: 'newUsers', name: 'Новые', color: 'var(--success)' },
                          ]}
                        />
                      </div>
                      <div style={{ ...styles.section, marginBottom: 16 }}>
                        <h2 style={styles.totalsHeading}>
                          Динамика: активные, новые и открыто отделом продаж
                        </h2>
                        <TimeSeriesChart
                          idPrefix="edu-getcourse-ops"
                          data={getcourseChartData}
                          height={320}
                          lines={[
                            { key: 'active', name: 'Кол-во активных пользователей', color: 'var(--accent)' },
                            { key: 'newUsers', name: 'Кол-во новых пользователей', color: 'var(--success)' },
                            { key: 'opened', name: 'Открыто отделом продаж', color: 'var(--warning)' },
                          ]}
                        />
                      </div>
                    </>
                  )}
                  {(getcourseTab?.summary?.base_total != null && getcourseTab.summary.base_total !== '') && (
                    <div style={styles.kpiRow}>
                      <KPICard
                        title="База всего"
                        value={String(getcourseTab.summary.base_total)}
                      />
                    </div>
                  )}
                  {(getcourseTab?.summary?.paid_purchases || []).length > 0 && (
                    <div style={{ ...styles.section, marginTop: 16, marginBottom: 8 }}>
                      <h2 style={styles.totalsHeading}>Куплено за деньги</h2>
                      <div style={styles.tableWrap}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              <th style={styles.th}>Месяц</th>
                              <th style={styles.th}>Заказы / сумма</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getcourseTab.summary.paid_purchases.map((p, i) => (
                              <tr key={`${p.month}-${i}`}>
                                <td style={styles.td}>{p.month}</td>
                                <td style={styles.td}>{p.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {sheetTabs.length > 0 && (
                <div style={styles.tabsBar} role="tablist" aria-label={isAds ? 'Листы рекламы' : 'Месяцы обучения'}>
                  {sheetTabs.map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={sheetActive === i}
                      onClick={() => setSheetActive(i)}
                      style={{ ...styles.tabBtn, ...(sheetActive === i ? styles.tabActive : {}) }}
                    >
                      {t.title}
                    </button>
                  ))}
                </div>
              )}

              {!isAds && !isGetcourse && data && totalsKpis && totalsKpis.length > 0 && (
                <div style={styles.totalsBlock}>
                  <h2 style={styles.totalsHeading}>
                    Итого за {current?.title || 'период'}
                  </h2>
                  <div style={styles.kpiRow}>
                    {totalsKpis.map((item, idx) => (
                      <KPICard key={`${item.title}-${idx}`} title={item.title} value={item.value} />
                    ))}
                  </div>
                </div>
              )}

              {data ? (
                <div style={styles.section} className="dash-section">
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          {!isGrid && (
                            <th style={{ ...styles.th, ...styles.thCorner }} />
                          )}
                          {(data.headers || []).map((h, i) => (
                            <th key={i} style={{ ...styles.th, ...(i === 0 && isGrid ? styles.thFirstSticky : {}) }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(data.rows || []).map((row, ri) => (
                          <tr key={ri} style={hlSet.has(ri) ? styles.hlRow : undefined}>
                            {!isGrid && (
                              <td
                                style={{
                                  ...styles.td,
                                  ...styles.tdStickyLeft,
                                  fontWeight: 600,
                                  color: 'var(--text-secondary)',
                                  background: rowBg(ri),
                                }}
                              >
                                {(data.row_headers || [])[ri] || ''}
                              </td>
                            )}
                            {row.map((cell, ci) => (
                              <td
                                key={ci}
                                style={{
                                  ...styles.td,
                                  ...(isGrid && ci === 0
                                    ? { ...styles.tdStickyLeft, background: rowBg(ri) }
                                    : {}),
                                }}
                              >
                                <CellValue cell={cell} />
                              </td>
                            ))}
                          </tr>
                        ))}
                        {(!data.rows || data.rows.length === 0) && (
                          <tr><td colSpan={(data.headers?.length || 0) + (isGrid ? 0 : 1)} style={{ ...styles.td, textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>Нет данных</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : current?.error ? (
                <div style={styles.info}>{current.error}</div>
              ) : !sectionError ? (
                <div style={styles.info}>Нет данных для отображения</div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 1400, margin: '0 auto', paddingBottom: 8 },
  pageHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 14,
    marginBottom: 16,
  },
  h1: {
    color: 'var(--text-primary)',
    fontSize: 26,
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.03em',
    fontFamily: 'var(--font-display)',
  },
  infoBtn: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'transform 0.2s ease, border-color 0.2s, box-shadow 0.2s',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(0,0,0,.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalPanel: {
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    maxWidth: 460,
    width: '100%',
    boxShadow: 'var(--shadow-lg)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 18px',
    borderBottom: '1px solid var(--border)',
  },
  modalTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-muted)',
    fontSize: 26,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
  },
  modalBody: {
    padding: 18,
    display: 'grid',
    gap: 10,
  },
  linkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '11px 16px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 600,
  },
  metaBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px 12px',
    marginBottom: 16,
    padding: '11px 16px',
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    boxShadow: 'var(--shadow-sm)',
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: 'var(--text-muted)',
  },
  metaValue: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    wordBreak: 'break-word',
  },
  error: {
    color: 'var(--danger)',
    background: 'rgba(248,113,113,.1)',
    border: '1px solid rgba(248,113,113,.25)',
    padding: '12px 16px',
    borderRadius: 12,
    marginBottom: 16,
    fontSize: 13,
    boxShadow: 'var(--shadow-sm)',
  },
  info: {
    color: 'var(--text-muted)',
    padding: 24,
    fontSize: 14,
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    boxShadow: 'var(--shadow-sm)',
  },
  yearTabsBar: {
    display: 'flex',
    gap: 6,
    marginBottom: 18,
    flexWrap: 'wrap',
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 6,
    boxShadow: 'var(--shadow-sm)',
    width: 'fit-content',
  },
  yearTabBtn: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: 10,
    padding: '10px 26px',
    fontSize: 14,
    cursor: 'pointer',
    fontWeight: 600,
    letterSpacing: 0.3,
    transition: 'transform 0.18s ease, background 0.2s, color 0.2s, box-shadow 0.2s',
  },
  yearTabActive: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    fontWeight: 700,
    boxShadow: 'var(--shadow-sm)',
  },
  sectionTabsBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 18,
    flexWrap: 'wrap',
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 5,
    boxShadow: 'var(--shadow-sm)',
    width: 'fit-content',
  },
  sectionTabBtn: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: 10,
    padding: '9px 18px',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'transform 0.18s ease, background 0.2s, color 0.2s, box-shadow 0.2s',
  },
  sectionTabActive: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    fontWeight: 600,
    boxShadow: 'var(--shadow-sm)',
  },
  cellLink: {
    color: 'var(--accent)',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    fontWeight: 600,
  },
  tabsBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 24,
    flexWrap: 'wrap',
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 5,
    boxShadow: 'var(--shadow-sm)',
  },
  tabBtn: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: 10,
    padding: '9px 18px',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'transform 0.18s ease, background 0.2s, color 0.2s, box-shadow 0.2s',
  },
  tabActive: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    fontWeight: 600,
    boxShadow: 'var(--shadow-sm)',
  },
  totalsBlock: {
    marginBottom: 22,
  },
  totalsHeading: {
    color: 'var(--text-primary)',
    fontSize: 15,
    fontWeight: 600,
    margin: '0 0 14px 0',
    letterSpacing: -0.2,
  },
  getcourseHint: {
    margin: '0 0 4px 0',
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--text-secondary)',
    maxWidth: 920,
  },
  kpiRow: {
    display: 'flex',
    gap: 14,
    flexWrap: 'wrap',
    marginBottom: 0,
  },
  section: {
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 20,
    overflow: 'hidden',
    marginBottom: 8,
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    color: 'var(--text-muted)',
    borderBottom: '1px solid var(--border)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: `linear-gradient(180deg, var(--bg-card) 0%, var(--bg-card) 100%)`,
    backdropFilter: 'blur(8px)',
    position: 'sticky',
    top: 0,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    zIndex: 1,
  },
  /** Левый верхний угол: закреплён и по горизонтали, и по вертикали при прокрутке */
  thCorner: {
    left: 0,
    zIndex: 4,
    minWidth: 180,
    maxWidth: 300,
  },
  /** Первая колонка в режиме grid — заголовок */
  thFirstSticky: {
    left: 0,
    zIndex: 3,
    boxShadow: '2px 0 6px rgba(0,0,0,.18)',
  },
  td: {
    padding: '10px 14px', color: 'var(--text-primary)',
    borderBottom: `1px solid var(--border)`, whiteSpace: 'nowrap',
  },
  /** Первая колонка (названия строк / первая ячейка grid) */
  tdStickyLeft: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    minWidth: 180,
    maxWidth: 300,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    boxShadow: '2px 0 6px rgba(0,0,0,.12)',
  },
  hlRow: { background: HL_ROW_BG },
};
